// src/pages/Admin/Views/Affiliates/AffiliateManagement.jsx
import React, { useEffect, useState } from 'react';
import { Search, Edit, CheckCircle2, XCircle, Users } from 'lucide-react';

// API Services
import { 
    getAffiliatesList, 
    updateAffiliateStatus, 
    assignPlanToAffiliate,
    getContractorsList,
    assignPlanToContractor
} from '../../../../services/referralApiServices';
import { getCommissionPlans } from '../../../../services/referralApiServices'; 

// shadcn/ui components
import { Card, CardHeader, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { Label } from '../../../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { useToast } from '../../../../hooks/use-toast'; 

const AffiliateManagement = () => {
    // --- Data State ---
    const [affiliates, setAffiliates] = useState([]);
    const [contractors, setContractors] = useState([]);
    const [commissionPlans, setCommissionPlans] = useState([]);
    
    // --- UI State ---
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('affiliates');
    
    // --- Modal State ---
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUserType, setSelectedUserType] = useState('AFFILIATE'); // 'AFFILIATE' or 'CONTRACTOR'
    const [selectedPlanId, setSelectedPlanId] = useState('DEFAULT');
    const [isSaving, setIsSaving] = useState(false);

    const { toast } = useToast();

    // --- Data Fetching ---
    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [affData, contData, plansData] = await Promise.all([
                getAffiliatesList(searchTerm),
                getContractorsList(),
                // Fetch active plans. We fetch both types so the modal can show the correct ones.
                getCommissionPlans({ is_active: true })
            ]);
            setAffiliates(affData || []);
            setContractors(contData || []);
            setCommissionPlans(plansData || []);
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: "Failed to load management data." });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        // Just refresh affiliates based on search term (assuming contractors list is small enough to not need backend search)
        getAffiliatesList(searchTerm).then(data => setAffiliates(data || []));
    };

    // --- Modal Handlers ---
    const openManageModal = (user, type) => {
        setSelectedUser(user);
        setSelectedUserType(type);
        setSelectedPlanId(user.custom_plan_id ? user.custom_plan_id.toString() : 'DEFAULT');
        setIsManageModalOpen(true);
    };

    const handleSaveChanges = async () => {
        if (!selectedUser) return;
        setIsSaving(true);
        
        try {
            const currentPlanIdStr = selectedUser.custom_plan_id ? selectedUser.custom_plan_id.toString() : 'DEFAULT';
            
            if (selectedPlanId !== currentPlanIdStr) {
                const payloadPlanId = selectedPlanId === 'DEFAULT' ? null : parseInt(selectedPlanId, 10);
                
                // Route to the correct API based on user type
                if (selectedUserType === 'AFFILIATE') {
                    await assignPlanToAffiliate(selectedUser.id, payloadPlanId);
                } else if (selectedUserType === 'CONTRACTOR') {
                    await assignPlanToContractor(selectedUser.id, payloadPlanId);
                }
            }

            toast({ variant: "success", title: "Success", description: "Commission plan updated successfully." });
            setIsManageModalOpen(false);
            fetchAllData(); 
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: "Failed to update commission plan." });
        } finally {
            setIsSaving(false);
        }
    };

    // Status toggle (Only strictly applicable to Affiliates right now based on your backend)
    const toggleStatus = async (isActive) => {
        if (selectedUserType !== 'AFFILIATE') {
            toast({ description: "Admin/Contractor status must be managed in the Admin Users tab." });
            return;
        }
        try {
            await updateAffiliateStatus(selectedUser.id, { is_active: isActive, reason: `Manual update` });
            setSelectedUser(prev => ({ ...prev, is_active: isActive }));
            toast({ description: `Affiliate marked as ${isActive ? 'Active' : 'Suspended'}.` });
            fetchAllData();
        } catch (error) {
            toast({ variant: "destructive", description: "Failed to update status." });
        }
    };

    // Filter plans dynamically based on who we are editing
    const availablePlans = commissionPlans.filter(plan => 
        plan.target_partner_type === selectedUserType
    );

    return (
        <div className="p-6 space-y-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Partner Management</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage external Affiliates and internal Sales Contractors.
                    </p>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList className="grid w-full md:w-[400px] grid-cols-2">
                    <TabsTrigger value="affiliates">External Affiliates</TabsTrigger>
                    <TabsTrigger value="contractors">Sales Contractors</TabsTrigger>
                </TabsList>

                {/* --- TAB 1: AFFILIATES --- */}
                <TabsContent value="affiliates" className="space-y-4">
                    <Card>
                        <CardHeader className="pb-4">
                            <form onSubmit={handleSearch} className="flex gap-2">
                                <Input 
                                    placeholder="Search affiliates by name, email..." 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="max-w-md"
                                />
                                <Button type="submit" variant="secondary">
                                    <Search className="w-4 h-4 mr-2" /> Search
                                </Button>
                            </form>
                        </CardHeader>
                        <CardContent>
                            <div className="border rounded-md">
                                <Table>
                                    <TableHeader className="bg-muted/50">
                                        <TableRow>
                                            <TableHead>Affiliate Name</TableHead>
                                            <TableHead>Contact Info</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Commission Plan</TableHead>
                                            <TableHead className="text-right">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow><TableCell colSpan={5} className="text-center py-8">Loading...</TableCell></TableRow>
                                        ) : affiliates.map((aff) => (
                                            <TableRow key={aff.id}>
                                                <TableCell className="font-medium text-foreground">
                                                    {aff.first_name} {aff.last_name}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-sm">{aff.email}</div>
                                                    <div className="text-xs text-muted-foreground">{aff.phone}</div>
                                                </TableCell>
                                                <TableCell>
                                                    {aff.is_suspended ? <Badge variant="destructive">Suspended</Badge> : aff.is_active ? <Badge className="bg-green-600">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                                                </TableCell>
                                                <TableCell>
                                                    {aff.custom_plan_id ? <Badge variant="outline" className="border-primary text-primary">Custom Plan</Badge> : <span className="text-sm text-muted-foreground">Default</span>}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="sm" onClick={() => openManageModal(aff, 'AFFILIATE')}>
                                                        <Edit className="w-4 h-4 mr-2 text-muted-foreground" /> Manage
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* --- TAB 2: SALES CONTRACTORS --- */}
                <TabsContent value="contractors" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-2">
                                <Users className="w-5 h-5 text-muted-foreground" />
                                <h3 className="text-lg font-semibold">Admin Sales Contractors</h3>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="border rounded-md">
                                <Table>
                                    <TableHeader className="bg-muted/50">
                                        <TableRow>
                                            <TableHead>Contractor Name</TableHead>
                                            <TableHead>Admin Email</TableHead>
                                            <TableHead>Commission Plan</TableHead>
                                            <TableHead className="text-right">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow><TableCell colSpan={4} className="text-center py-8">Loading...</TableCell></TableRow>
                                        ) : contractors.map((contractor) => (
                                            <TableRow key={contractor.id}>
                                                <TableCell className="font-medium text-foreground">
                                                    {contractor.user_name}
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">
                                                    {contractor.email}
                                                </TableCell>
                                                <TableCell>
                                                    {contractor.custom_plan_id ? <Badge variant="outline" className="border-primary text-primary">Custom Plan</Badge> : <span className="text-sm text-muted-foreground">Default</span>}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="sm" onClick={() => openManageModal(contractor, 'CONTRACTOR')}>
                                                        <Edit className="w-4 h-4 mr-2 text-muted-foreground" /> Assign Plan
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {!loading && contractors.length === 0 && (
                                            <TableRow><TableCell colSpan={4} className="h-32 text-center text-muted-foreground">No active Sales Contractors found in Admin Users.</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* --- UNIFIED MANAGE MODAL --- */}
            <Dialog open={isManageModalOpen} onOpenChange={setIsManageModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Manage {selectedUserType === 'AFFILIATE' ? 'Affiliate' : 'Contractor'}</DialogTitle>
                        <DialogDescription>
                            Update settings for {selectedUserType === 'AFFILIATE' ? `${selectedUser?.first_name} ${selectedUser?.last_name}` : selectedUser?.user_name}.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedUser && (
                        <div className="space-y-6 py-4">
                            
                            {/* Status Toggles (Affiliates Only) */}
                            {selectedUserType === 'AFFILIATE' && (
                                <div className="space-y-3">
                                    <Label>Account Status</Label>
                                    <div className="flex gap-2">
                                        {selectedUser.is_active ? (
                                            <Button variant="outline" className="w-full text-red-600 hover:bg-red-50" onClick={() => toggleStatus(false)}>
                                                <XCircle className="w-4 h-4 mr-2" /> Suspend Account
                                            </Button>
                                        ) : (
                                            <Button variant="outline" className="w-full text-green-600 hover:bg-green-50" onClick={() => toggleStatus(true)}>
                                                <CheckCircle2 className="w-4 h-4 mr-2" /> Activate Account
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Plan Assignment */}
                            <div className={`space-y-2 ${selectedUserType === 'AFFILIATE' ? 'pt-4 border-t' : ''}`}>
                                <Label>Commission Plan Assignment</Label>
                                <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a commission plan" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="DEFAULT">-- Default {selectedUserType === 'CONTRACTOR' ? 'Contractor' : 'Affiliate'} Rules --</SelectItem>
                                        {availablePlans.map(plan => (
                                            <SelectItem key={plan.id} value={plan.id.toString()}>
                                                {plan.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {availablePlans.length === 0 && (
                                    <p className="text-xs text-destructive mt-1">No active plans found for this partner type. Please create one first.</p>
                                )}
                            </div>
                            
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsManageModalOpen(false)} disabled={isSaving}>Cancel</Button>
                        <Button onClick={handleSaveChanges} disabled={isSaving}>
                            {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AffiliateManagement;